import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import styled, { css, keyframes } from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { getOfficeLayout } from '@features/desks/services/DeskBooking';
import {
  SignageDeskOccupancy,
  SignageFloorPlan,
  SignageNameDisplay,
  SignageOccupancy,
  SignageOccupant,
} from '../data/signage-service';

export interface SignageDeskFocus {
  deskId: number;
  // Changes on every search selection so focusing the same desk twice still fires
  nonce: number;
}

export interface SignageDeskPing {
  deskIds: number[];
  colour: string;
  // Changes on every pill tap so re-pinging the same role still fires
  nonce: number;
}

export interface SignageFloorMapHandle {
  zoomIn(): void;
  zoomOut(): void;
}

interface Props {
  floorPlan: SignageFloorPlan;
  occupancy: SignageOccupancy | null;
  activePeriod: 'AM' | 'PM';
  // "HH:mm" in the device's local time, for meeting room busy/free shading
  currentTime: string;
  focusDesk?: SignageDeskFocus | null;
  // Pulse a ring on these desks (safety-role pills), resetting to the overview first
  pingDesks?: SignageDeskPing | null;
  // The zoom buttons live in the sub-header, outside this component
  onZoomPercentChange?: (percent: number) => void;
}

interface PositionedDesk {
  deskId: number;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

interface LegacyLayout {
  floorPlan: string;
  width?: number;
  height?: number;
  desks?: { deskId: number; label?: string; x: number; y: number; width?: number; height?: number; rotate?: number }[];
  areas?: { label: string; x: number; y: number; width?: number; height?: number }[];
}

function floorPlanImageUrl(path: string): string {
  if (path.startsWith('s3/')) {
    return `https://team-today-floorplans.s3.eu-west-2.amazonaws.com/${path}`;
  }
  return path;
}

const DEFAULT_DESK_SIZE = 32;
// A walked-away-from screen returns to the fitted overview on its own.
const RESET_VIEW_AFTER_MS = 30_000;
const MAX_ZOOM = 5;
const FOCUS_ZOOM = 2.5;
const PING_DURATION_MS = 12_000;

export const SignageFloorMap = React.forwardRef<SignageFloorMapHandle, Props>(function SignageFloorMap(
  { floorPlan, occupancy, activePeriod, currentTime, focusDesk, pingDesks, onZoomPercentChange }: Props,
  handleRef,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null);
  const [legacyLayout, setLegacyLayout] = useState<LegacyLayout | null>(null);
  const [selectedDeskId, setSelectedDeskId] = useState<number | null>(null);

  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const resetTimerRef = useRef<number | null>(null);

  useImperativeHandle(handleRef, () => ({
    zoomIn: () => transformRef.current?.zoomIn(),
    zoomOut: () => transformRef.current?.zoomOut(),
  }), []);

  // The fitted overview is the resting state. Any touch/mouse zoom or pan
  // schedules a snap back to it after 30s without further interaction; the
  // reset itself lands on the identity transform, which clears the timer.
  const handleTransformed = (_ref: ReactZoomPanPinchRef, state: { scale: number; positionX: number; positionY: number }) => {
    onZoomPercentChange?.(Math.round(state.scale * 100));
    if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = null;
    const isFittedView = Math.abs(state.scale - 1) < 0.01
      && Math.abs(state.positionX) < 1 && Math.abs(state.positionY) < 1;
    if (!isFittedView) {
      resetTimerRef.current = window.setTimeout(() => {
        setSelectedDeskId(null);
        transformRef.current?.resetTransform();
      }, RESET_VIEW_AFTER_MS);
    }
  };

  useEffect(() => () => {
    if (resetTimerRef.current !== null) window.clearTimeout(resetTimerRef.current);
  }, []);

  // A colleague picked from the search bar: highlight their desk, open its
  // popup and zoom in on it.
  useEffect(() => {
    if (!focusDesk) return;
    setSelectedDeskId(focusDesk.deskId);
    try {
      transformRef.current?.zoomToElement(`signage-desk-${focusDesk.deskId}`, FOCUS_ZOOM);
    } catch { /* desk not on this floor's plan */ }
  }, [focusDesk]);

  // A safety pill tapped: reset to the fitted overview so every pinged desk
  // is on screen, then pulse them for a few seconds.
  const [activePing, setActivePing] = useState<{ ids: Set<number>; colour: string } | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!pingDesks || pingDesks.deskIds.length === 0) return;
    setSelectedDeskId(null);
    transformRef.current?.resetTransform();
    setActivePing({ ids: new Set(pingDesks.deskIds), colour: pingDesks.colour });
    if (pingTimerRef.current !== null) window.clearTimeout(pingTimerRef.current);
    pingTimerRef.current = window.setTimeout(() => setActivePing(null), PING_DURATION_MS);
  }, [pingDesks]);
  useEffect(() => () => {
    if (pingTimerRef.current !== null) window.clearTimeout(pingTimerRef.current);
  }, []);

  const usesInlineSvg = !!floorPlan.officeFloorPlan;

  useEffect(() => {
    setLegacyLayout(null);
    setImageSize(null);
    setSelectedDeskId(null);
    if (!usesInlineSvg && floorPlan.officePlan) {
      let cancelled = false;
      getOfficeLayout(floorPlan.officePlan)
        .then(layout => { if (!cancelled) setLegacyLayout(layout); })
        .catch(() => { /* keep showing nothing rather than crash the screen */ });
      return () => { cancelled = true; };
    }
  }, [floorPlan.floorId, floorPlan.officePlan, usesInlineSvg]);

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      setContainerSize({ w: containerRef.current.clientWidth, h: containerRef.current.clientHeight });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const imageSrc = useMemo(() => {
    if (usesInlineSvg && floorPlan.officeFloorPlan) {
      return 'data:image/svg+xml,' + encodeURIComponent(floorPlan.officeFloorPlan);
    }
    if (legacyLayout?.floorPlan) {
      return floorPlanImageUrl(legacyLayout.floorPlan);
    }
    return null;
  }, [usesInlineSvg, floorPlan.officeFloorPlan, legacyLayout]);

  // DB coordinates win; legacy plan JSON fills in for desks that predate them.
  const desks: PositionedDesk[] = useMemo(() => {
    const legacyByDeskId = new Map<number, { x: number; y: number; width?: number; height?: number; rotate?: number; label?: string }>();
    (legacyLayout?.desks ?? []).forEach(d => legacyByDeskId.set(d.deskId, d));

    return floorPlan.desks
      .filter(d => d.active)
      .map(d => {
        const legacy = legacyByDeskId.get(d.id);
        const x = d.x ?? legacy?.x;
        const y = d.y ?? legacy?.y;
        if (x === undefined || x === null || y === undefined || y === null) return null;
        return {
          deskId: d.id,
          label: d.label,
          x: Number(x),
          y: Number(y),
          width: Number(d.width ?? legacy?.width ?? DEFAULT_DESK_SIZE),
          height: Number(d.height ?? legacy?.height ?? DEFAULT_DESK_SIZE),
          rotation: Number(d.rotation ?? legacy?.rotate ?? 0),
        };
      })
      .filter((d): d is PositionedDesk => d !== null);
  }, [floorPlan.desks, legacyLayout]);

  const occupancyByDesk = useMemo(() => {
    const map = new Map<number, SignageDeskOccupancy>();
    (occupancy?.desks ?? []).forEach(d => map.set(d.deskId, d));
    return map;
  }, [occupancy]);

  const busyRoomIds = useMemo(() => {
    const busy = new Set<string>();
    (occupancy?.meetingRooms ?? []).forEach(room => {
      if (room.busy.some(block => block.start <= currentTime && currentTime < block.end)) {
        busy.add(room.meetingRoomId);
      }
    });
    return busy;
  }, [occupancy, currentTime]);

  const planWidth = imageSize?.w ?? legacyLayout?.width ?? null;
  const planHeight = imageSize?.h ?? legacyLayout?.height ?? null;
  const scale = containerSize && planWidth && planHeight
    ? Math.min(containerSize.w / planWidth, containerSize.h / planHeight)
    : 1;

  const nameDisplay: SignageNameDisplay = occupancy?.nameDisplay ?? 'FULL_NAMES';

  const selectedDesk = selectedDeskId !== null ? desks.find(d => d.deskId === selectedDeskId) ?? null : null;
  const selectedOccupancy = selectedDeskId !== null ? occupancyByDesk.get(selectedDeskId) : undefined;
  const selectedOccupant = selectedOccupancy
    ? (activePeriod === 'AM' ? selectedOccupancy.am : selectedOccupancy.pm)
    : null;

  return (
    <MapContainer ref={containerRef}>
      <TransformWrapper
        ref={transformRef}
        minScale={1}
        maxScale={MAX_ZOOM}
        initialScale={1}
        onTransformed={handleTransformed}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ width: '100%', height: '100%' }}
        >
          <Canvas onClick={() => setSelectedDeskId(null)}>
      {imageSrc && (
        <ScaledPlan style={{
          width: planWidth ?? undefined,
          height: planHeight ?? undefined,
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}>
          <PlanImage
            src={imageSrc}
            alt="Floor plan"
            onLoad={e => {
              const img = e.currentTarget;
              if (img.naturalWidth && img.naturalHeight) {
                setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
              }
            }}
          />

          {(legacyLayout?.areas ?? floorPlan.areas.map(a => ({ label: a.label, x: a.x, y: a.y, width: a.width ?? undefined, height: a.height ?? undefined })))
            .filter(a => a.x !== null && a.y !== null)
            .map((area, index) => (
              <AreaRect key={`area-${index}`} style={{
                left: Number(area.x),
                top: Number(area.y),
                width: area.width ? Number(area.width) : undefined,
                height: area.height ? Number(area.height) : undefined,
              }}>
                <AreaLabel>{area.label}</AreaLabel>
              </AreaRect>
            ))}

          {floorPlan.meetingRooms
            // Same guard as MeetingRoomSpotOverlay on the main floor plan, so
            // both pages show exactly the same set of rooms (including its
            // quirk of treating 0 as unset).
            .filter(room => !!room.x && !!room.y && !!room.width && !!room.height)
            .map(room => (
              <RoomRect key={room.id} $busy={busyRoomIds.has(room.id)} style={{
                left: room.x,
                top: room.y,
                width: Number(room.width),
                height: Number(room.height),
              }}>
                <RoomLabel>{room.name}</RoomLabel>
                <RoomStatus>{busyRoomIds.has(room.id) ? 'In use' : 'Free'}</RoomStatus>
              </RoomRect>
            ))}

          {desks.map(desk => {
            const deskOccupancy = occupancyByDesk.get(desk.deskId);
            const occupant = activePeriod === 'AM' ? deskOccupancy?.am : deskOccupancy?.pm;
            return (
              <DeskSpot
                key={desk.deskId}
                id={`signage-desk-${desk.deskId}`}
                $selected={desk.deskId === selectedDeskId}
                $pinged={!!activePing?.ids.has(desk.deskId)}
                onClick={e => {
                  e.stopPropagation();
                  setSelectedDeskId(prev => prev === desk.deskId ? null : desk.deskId);
                }}
                style={{
                  left: desk.x,
                  top: desk.y,
                  width: desk.width,
                  minHeight: desk.height,
                  transform: `rotate(${desk.rotation}deg)`,
                  ...(activePing?.ids.has(desk.deskId) ? { '--ping-color': activePing.colour } as React.CSSProperties : {}),
                }}
              >
                <DeskContent occupant={occupant ?? null} deskLabel={desk.label} nameDisplay={nameDisplay} />
              </DeskSpot>
            );
          })}

          {selectedDesk && (
            <DeskPopup style={{
              left: selectedDesk.x + selectedDesk.width + 10,
              top: selectedDesk.y,
            }}>
              {selectedOccupant && nameDisplay === 'FULL_NAMES' && selectedOccupant.displayName && (
                <PopupName>{selectedOccupant.displayName}</PopupName>
              )}
              {selectedOccupant && nameDisplay === 'INITIALS' && selectedOccupant.initials && (
                <PopupName>{selectedOccupant.initials.toUpperCase()}</PopupName>
              )}
              <PopupDetail>
                {selectedDesk.label}
                {selectedOccupant
                  ? (nameDisplay === 'RESERVED_ONLY' ? ' · Reserved' : '')
                  : ' · Free'}
              </PopupDetail>
            </DeskPopup>
          )}
        </ScaledPlan>
      )}
          </Canvas>
        </TransformComponent>
      </TransformWrapper>
    </MapContainer>
  );
});

function DeskContent({ occupant, deskLabel, nameDisplay }: {
  occupant: SignageOccupant | null;
  deskLabel: string;
  nameDisplay: SignageNameDisplay;
}) {
  if (!occupant) {
    return <DeskLabel>{deskLabel}</DeskLabel>;
  }
  // Booked desks show a navy avatar circle with white initials (blank in
  // RESERVED_ONLY mode); the popup carries the full name on tap.
  return (
    <OccupantWrapper>
      <AvatarCircle $firstAider={occupant.firstAider} title={occupant.firstAider ? 'First aider' : undefined}>
        {occupant.initials ? occupant.initials.toUpperCase() : ''}
      </AvatarCircle>
      {occupant.fireWarden && <RoleIcon src="/assets/icons/fire-warden.svg" alt="Fire warden" title="Fire warden" />}
    </OccupantWrapper>
  );
}

const MapContainer = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  background: white;
  touch-action: none;
`;

const Canvas = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const ScaledPlan = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform-origin: center center;
`;

const PlanImage = styled.img`
  display: block;
  user-select: none;
  pointer-events: none;
`;

const AreaRect = styled.div`
  position: absolute;
  border: 2px dashed ${Colours.lightGrey};
  border-radius: 8px;
  pointer-events: none;
`;

const AreaLabel = styled.div`
  position: absolute;
  top: 6px;
  left: 10px;
  font-size: 20px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  white-space: nowrap;
`;

// Matches the MeetingRoomOverlay treatment on the main floor plan: transparent
// box, 3px green border, radius 16, content top-left. Busy rooms swap to orange.
const RoomRect = styled.div<{ $busy: boolean }>`
  position: absolute;
  box-sizing: border-box;
  border-radius: 16px;
  border: 3px solid ${p => p.$busy ? Colours.orange : Colours.green};
  background: ${p => p.$busy ? 'rgba(240, 130, 34, 0.1)' : Colours.transparent};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 12px 16px;
  overflow: hidden;
`;

const RoomLabel = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${Colours.black};
`;

const RoomStatus = styled.div`
  font-size: 17px;
  color: ${Colours.darkGrey};
`;

// Two ring waves radiating outwards, big enough to catch the eye from across
// a room even at the fitted zoom level.
const pingPulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--ping-color), 0 0 0 0 var(--ping-color); }
  50% { box-shadow: 0 0 0 22px transparent, 0 0 0 0 var(--ping-color); }
  99% { box-shadow: 0 0 0 22px transparent, 0 0 0 44px transparent; }
  100% { box-shadow: 0 0 0 0 transparent, 0 0 0 0 transparent; }
`;

// White desk card per the signage design: light border, navy avatar for
// occupants, plain label when free, blue border when highlighted.
const DeskSpot = styled.div<{ $selected: boolean; $pinged?: boolean }>`
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  box-sizing: border-box;
  border: 2px solid ${p => p.$selected ? Colours.blue : Colours.lightGrey};
  background: white;
  color: ${p => p.$selected ? Colours.blue : Colours.black};
  font-family: "proxima-nova", sans-serif;
  padding: 2px;
  text-align: center;
  ${p => p.$pinged ? css`
    animation: ${pingPulse} 1.6s ease-out infinite;
    border-color: var(--ping-color);
    border-width: 3px;
    z-index: 50;
  ` : ''}
`;

const DeskLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: inherit;
`;

const OccupantWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 4px;
  max-width: 100%;
`;

// Navy avatar circle with white uppercase soleil-bold initials. First aiders
// are marked by a thick green ring instead of a separate icon.
const AvatarCircle = styled.span<{ $firstAider?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  box-sizing: border-box;
  border-radius: 100%;
  border: ${p => p.$firstAider ? `6px solid ${Colours.green}` : 'none'};
  background: ${Colours.veryDarkBlue};
  color: white;
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 16px;
`;

// The same icon asset the app's FireWardenBadge atom uses.
const RoleIcon = styled.img`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: block;
`;

const DeskPopup = styled.div`
  position: absolute;
  z-index: 200;
  background: ${Colours.veryDarkBlue};
  color: white;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  white-space: nowrap;
`;

const PopupName = styled.div`
  font-family: "proxima-nova", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
`;

const PopupDetail = styled.div`
  font-family: "proxima-nova", sans-serif;
  font-size: 14px;
  line-height: 20px;
  opacity: 0.85;
`;

